import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GrundstueckRubrikComponentVM } from './grundstueck-rubrik-component-v-m';
import { GrundstueckListEntry } from '../../core/av-api';
import { LastRechtRootEntryVm, StandardrechtViewmodelService } from '../../core/services/standardrecht-viewmodel.service';
import { ListLoader } from '../../shared/components/list-loader/list-loader';
import { ListLoaderInfiniteBuilder } from '../../shared/components/list-loader/list-loader-infinite';
import { AbstractComponent } from '../../shared/components/abstract-component/abstract.component';
import { AppConfigService } from '../../core/services/app-config.service';
import { EigentumanteilMasterDetailVM, EigentumViewmodelService } from '../../core/services/eigentum-viewmodel.service';
import { GrundpfandrechtViewmodelService, RootBelastungVm } from '../../core/services/grundpfandrecht-viewmodel.service';
import { ListLoaderObservable } from '../../shared/components/list-loader/list-loader-observable';
import { AppPermissionService } from 'app/core/services/app-permission.service';
import { map, tap } from 'rxjs/operators';
import { AbstractRangregelungVm, RangregelungViewmodelService } from 'app/core/services/rangregelung-viewmodel.service';
import { RubrikComponent } from '../../shared/components/rubrik/rubrik.component';
import { GrundstueckBeschreibungComponent } from '../beschreibung/grundstueck-beschreibung.component';
import { EigentumTableComponent } from '../../shared/widgets/eigentum-table/eigentum-table.component';
import { GrundstueckHaengigeComponent } from '../haengige/grundstueck-haengige.component';
import { StandardrechtTableComponent } from '../../shared/widgets/standardrecht-table/standardrecht-table.component';
import { RangregelungTableComponent } from '../../shared/widgets/rangregelung-table/rangregelung-table.component';
import { GrundpfandrechtTableComponent } from '../../shared/widgets/grundpfandrecht-table/grundpfandrecht-table.component';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'cap-grundstueck-rubriken',
  templateUrl: './grundstueck-rubriken.component.html',
  styleUrls: ['./grundstueck-rubriken.component.scss'],
  imports: [
    RubrikComponent,
    GrundstueckBeschreibungComponent,
    EigentumTableComponent,
    GrundstueckHaengigeComponent,
    StandardrechtTableComponent,
    RangregelungTableComponent,
    GrundpfandrechtTableComponent,
    NgClass,
    TranslatePipe
  ]
})
export class GrundstueckRubrikenComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  item: GrundstueckRubrikComponentVM;
  @Input()
  grundstueck: GrundstueckListEntry;

  showLableNichtOeffentlicheAnmerkungen: boolean;
  loadOption: 'ALLE' | 'HAENGIGE' = 'ALLE';
  eigentumErweitert = false;

  eigentumLoader: ListLoader<EigentumanteilMasterDetailVM>;
  eigentumErsterfassungUebergeben: boolean;

  dienstbarkeitListLoader: ListLoader<LastRechtRootEntryVm>;
  dienstbarkeitErsterfassungUebergeben: boolean;

  grundlastListLoader: ListLoader<LastRechtRootEntryVm>;
  grundlastErsterfassungUebergeben: boolean;

  anmerkungListLoader: ListLoader<LastRechtRootEntryVm>;
  anmerkungErsterfassungUebergeben: boolean;

  vormerkungListLoader: ListLoader<LastRechtRootEntryVm>;
  vormerkungErsterfassungUebergeben: boolean;

  rangregelungListLoader: ListLoader<AbstractRangregelungVm>;

  grundpfandrechteListLoader: ListLoader<RootBelastungVm>;
  grundpfandrechErsterfassungUebergeben: boolean;

  private readonly CHUNK_SIZE = 5;
  private readonly PAGE_SIZE = 5;
  private readonly CURRENT_PAGE = 1;

  constructor(
    private standardrechtViewmodelService: StandardrechtViewmodelService,
    private eigentumViewmodelService: EigentumViewmodelService,
    private rangregelungViewModelService: RangregelungViewmodelService,
    private grundpfandrechtViewmodelService: GrundpfandrechtViewmodelService,
    private permissionService: AppPermissionService,
    private appConfigService: AppConfigService
  ) {
    super();
  }

  ngOnInit(): void {
    this.showLableNichtOeffentlicheAnmerkungen = !this.permissionService.hasPermission('ANZEIGE_NICHT_OEFFENTLICHE_ANMERKUNGEN');
    this.addSub(
      this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
        this.loadData();
      })
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  setLoadOptions(option: 'ALLE' | 'HAENGIGE' = 'ALLE') {
    this.loadOption = option;
  }

  private loadData() {
    if (this.item.element === 'detailGrundstueckDienstbarkeiten') {
      this.dienstbarkeitListLoader = this.createDienstbarkeitenLoader();
    }
    if (this.item.element === 'detailGrundstueckGrundlasten') {
      this.grundlastListLoader = this.createGrundlastenLoader();
    }
    if (this.item.element === 'detailGrundstueckAnmerkungen') {
      this.anmerkungListLoader = this.createAnmerkungLoader();
    }
    if (this.item.element === 'detailGrundstueckVormerkungen') {
      this.vormerkungListLoader = this.createVormerkungLoader();
    }
    if (this.item.element === 'detailGrundstueckEigentum') {
      this.eigentumLoader = this.createEigentumLoader();
    }
    if (this.item.element === 'detailGrundstueckGrundpfandrechte') {
      this.grundpfandrechteListLoader = this.createPfandrechtLoader();
    }
    if (this.item.element === 'detailGrundstueckRangregelungen') {
      this.rangregelungListLoader = this.createRangregelungLoader();
    }
  }

  private createRangregelungLoader(): ListLoader<AbstractRangregelungVm> {
    const observable = (offset: number, limit: number) => {
      return this.rangregelungViewModelService
        .loadSichtByGrundstueckId(this.grundstueck.id, offset, limit, this.subscriptions)
        .pipe(map((res) => res.entries));
    };
    const loader = ListLoaderInfiniteBuilder.builder<AbstractRangregelungVm>()
      .setLookupItemFunc(observable)
      .setChunkSize(this.CHUNK_SIZE)
      .setPageSize(this.PAGE_SIZE)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPublishItemsWhenAllLoaded(true)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createEigentumLoader(): ListLoader<EigentumanteilMasterDetailVM> {
    const observable = (offset: number, limit: number) => {
      return this.eigentumViewmodelService
        .loadSichtByGrundstueckId(
          this.grundstueck.id, //
          this.eigentumErweitert, //
          true, //
          true, //
          offset, //
          limit, //
          this.subscriptions
        )
        .pipe(
          tap((res) => (this.eigentumErsterfassungUebergeben = res.ersterfassungUebergeben)),
          map((res) => res.entries)
        );
    };

    const loader = ListLoaderInfiniteBuilder.builder<EigentumanteilMasterDetailVM>()
      .setLookupItemFunc(observable)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPageSize(this.PAGE_SIZE)
      .setChunkSize(this.CHUNK_SIZE)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createDienstbarkeitenLoader(): ListLoader<LastRechtRootEntryVm> {
    const observable = (offset: number, limit: number) => {
      return this.standardrechtViewmodelService
        .loadSichtByGrundstueckId(this.grundstueck.id, 'DIENSTBARKEIT', offset, limit, this.subscriptions)
        .pipe(
          tap((res) => (this.dienstbarkeitErsterfassungUebergeben = res.ersterfassungUebergeben)),
          map((res) => res.entries)
        );
    };
    const loader = ListLoaderInfiniteBuilder.builder<LastRechtRootEntryVm>()
      .setLookupItemFunc(observable)
      .setChunkSize(this.CHUNK_SIZE)
      .setPageSize(this.PAGE_SIZE)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPublishItemsWhenAllLoaded(true)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createGrundlastenLoader(): ListLoader<LastRechtRootEntryVm> {
    const observable = (offset: number, limit: number) => {
      return this.standardrechtViewmodelService
        .loadSichtByGrundstueckId(this.grundstueck.id, 'GRUNDLAST', offset, limit, this.subscriptions)
        .pipe(
          tap((res) => (this.grundlastErsterfassungUebergeben = res.ersterfassungUebergeben)),
          map((res) => res.entries)
        );
    };
    const loader = ListLoaderInfiniteBuilder.builder<LastRechtRootEntryVm>()
      .setLookupItemFunc(observable)
      .setChunkSize(this.CHUNK_SIZE)
      .setPageSize(this.PAGE_SIZE)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPublishItemsWhenAllLoaded(true)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createAnmerkungLoader(): ListLoader<LastRechtRootEntryVm> {
    const observable = (offset: number, limit: number) => {
      return this.standardrechtViewmodelService
        .loadSichtByGrundstueckId(this.grundstueck.id, 'ANMERKUNG', offset, limit, this.subscriptions)
        .pipe(
          tap((res) => (this.anmerkungErsterfassungUebergeben = res.ersterfassungUebergeben)),
          map((res) => res.entries)
        );
    };
    const loader = ListLoaderInfiniteBuilder.builder<LastRechtRootEntryVm>()
      .setLookupItemFunc(observable)
      .setChunkSize(this.CHUNK_SIZE)
      .setPageSize(this.PAGE_SIZE)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPublishItemsWhenAllLoaded(true)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createVormerkungLoader(): ListLoader<LastRechtRootEntryVm> {
    const observable = (offset: number, limit: number) => {
      return this.standardrechtViewmodelService
        .loadSichtByGrundstueckId(this.grundstueck.id, 'VORMERKUNG', offset, limit, this.subscriptions)
        .pipe(
          tap((res) => (this.vormerkungErsterfassungUebergeben = res.ersterfassungUebergeben)),
          map((res) => res.entries)
        );
    };
    const loader = ListLoaderInfiniteBuilder.builder<LastRechtRootEntryVm>()
      .setLookupItemFunc(observable)
      .setChunkSize(this.CHUNK_SIZE)
      .setPageSize(this.PAGE_SIZE)
      .setCurrentPage(this.CURRENT_PAGE)
      .setPublishItemsWhenAllLoaded(true)
      .build();
    this.addLoader(loader);
    return loader;
  }

  private createPfandrechtLoader(): ListLoader<RootBelastungVm> {
    const observable = this.grundpfandrechtViewmodelService.loadSichtByGbGrundstueckId(this.grundstueck.id).pipe(
      tap((res) => (this.grundpfandrechErsterfassungUebergeben = res.ersterfassungUebergeben)),
      map((res) => res.entries)
    );
    return new ListLoaderObservable(5, 1, observable);
  }
}
